import type { ComponentNode } from '@api/types.gen'
import type { PipeEventProcessorDef, PipeInterface } from './usePipe'
import { ERROR_CONTEXTS, HALT_PROCESSOR_CHAIN, PipeDisplayMode } from './usePipe'
import { computed, inject, type ComputedRef } from 'vue'
import { evalRule } from './useLogic'
import { PAYMENT_EVENTS } from './usePaymentEvents'
import {
  writeStoredConsent,
  COOKIE_CONSENT_PENDING_VAR_KEY,
  type CookieConsentState,
} from '@render/lib/cookieConsent'

export { HALT_PROCESSOR_CHAIN } from './usePipe'

export const STOCK_EVENT_PROCESSORS: Record<string, PipeEventProcessorDef> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  eval_script: {
    type: 'eval_script',
    handler: async ({ args, data, pipe }) => {
      try {
        // AsyncFunction so author scripts can use top-level `await` (e.g.
        // `await new Promise(r => setTimeout(r, 1000))`). `new Function(...)`
        // would build a sync function and reject `await` as a SyntaxError.
        const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor
        const fn = new AsyncFunction('args', data)
        await fn(args)
      } catch (e) {
        pipe.trackError({
          error: e,
          context: ERROR_CONTEXTS.evalScript,
          metadata: {
            // Trimmed: author scripts can be large or sensitive.
            snippet: typeof data === 'string' ? data.slice(0, 200) : null,
          },
        })
      }
    },
  },
  navigate: {
    type: 'navigate',
    handler: async ({ data, pipe }) => {
      // TEMP: disabled in EDITOR mode while investigating navigation-link issue.
      if (
        [PipeDisplayMode.EDITOR, PipeDisplayMode.INTERACTIVE_VIEW].includes(pipe.currentMode.value)
      )
        return
      if (data?.pageId) {
        // 1. Await the actual navigation to the new page
        await pipe.navigateToPageId(data.pageId)

        // 2. Wipe the ghost selection that Android creates on the newly rendered DOM
        // We use a 50ms timeout to ensure the browser has finished rendering the
        // new page and the physical touch events have fully resolved.
        // If we need  to allow selection on page show, this might break things, but for now this is a targeted fix for the Android ghost selection issue.
        setTimeout(() => {
          if (typeof window === 'undefined' || !window.getSelection) return
          // NEVER clear the selection while a text-entry element is focused:
          // `removeAllRanges()` wipes a focused <input>/<textarea>/contenteditable's
          // caret/insertion point, leaving it focused-but-untypeable. This silently
          // broke autofocused inputs reached via navigation (useAutoFocus focuses the
          // new page's input ~16ms after navigation:show, before this 50ms fires).
          const active = document.activeElement as HTMLElement | null
          if (
            active instanceof HTMLInputElement ||
            active instanceof HTMLTextAreaElement ||
            active?.isContentEditable
          ) {
            return
          }
          window.getSelection()?.removeAllRanges()
        }, 50)
      }
    },
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  navigate_external: {
    type: 'navigate_external',
    handler: ({ data }) => {
      if (data && typeof data === 'string') window.open(data, '_blank')
    },
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  set_data: {
    type: 'set_data',
    handler: async ({ data, pipe }) => {
      console.log('set_data processor called with data:', data)
      if (!data || !data.key) return
      // The value may be a primitive (string/number/bool/array) or a
      // reference wrapper authored via RuleOperand: `{ field: 'var.foo' }`.
      // Resolve the reference through pipe.getData so the variable is set to
      // the CURRENT value of the referenced source, not the wrapper literal.
      const raw = data.value as unknown
      let resolved: unknown = raw
      if (raw !== null && typeof raw === 'object' && 'field' in (raw as Record<string, unknown>)) {
        const field = (raw as { field: unknown }).field
        const refKey =
          typeof field === 'string'
            ? field
            : Array.isArray(field) && typeof field[0] === 'string'
              ? field[0]
              : undefined
        resolved = refKey ? pipe.getData(refKey) : undefined
      }
      await pipe.setData(`var.${data.key}`, resolved)
    },
  },
  // Records the visitor's cookie-consent decision. Mirrors into the
  // `global.isCookieConsentPending` boolean variable (drives the banner's
  // visibility rule: true = consent undecided = show) and persists the
  // tri-state decision to the dedicated localStorage key (source of truth,
  // survives the landing reset — see @render/lib/cookieConsent). The Google
  // Consent Mode
  // signal is NOT fired here — that's Google's concern, so this emits
  // `cookie-consent:changed` and the GTM integration calls
  // gtag('consent','update',...). 'undecided' clears the key + re-shows the
  // banner ("change preferences") and intentionally does not touch gtag (the
  // prior signal stands until the visitor re-decides). Editor preview only
  // mirrors the var (no localStorage/emit); self-heals on reload.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  set_cookie_consent: {
    type: 'set_cookie_consent',
    handler: async ({ data, pipe }) => {
      const decision = (data as { decision?: CookieConsentState } | undefined)?.decision
      if (decision !== 'granted' && decision !== 'denied' && decision !== 'undecided') return
      // Per-distribution feature flag: cookie consent is OFF unless
      // `const.global.cookieConsentEnabled` is explicitly true. When off in a
      // real build, a stray "Change preferences / Reopen" control placed outside
      // the banner must not be able to re-show it — force not-pending and bail
      // (no persist/emit). Editor preview is exempt so authors can exercise the
      // banner. Absent → disabled.
      const consentEnabled =
        pipe.currentMode.value === PipeDisplayMode.EDITOR ||
        pipe.getData('const.global.cookieConsentEnabled') === true
      if (!consentEnabled) {
        await pipe.setData(`var.${COOKIE_CONSENT_PENDING_VAR_KEY}`, false)
        return
      }
      // Mirror the boolean in all modes so the visibility rule reacts
      // immediately (preview included): true only while undecided (Reopen),
      // false once granted/denied.
      await pipe.setData(`var.${COOKIE_CONSENT_PENDING_VAR_KEY}`, decision === 'undecided')
      if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return
      writeStoredConsent(decision, Date.now())
      if (decision === 'undecided') return
      pipe.emit('cookie-consent:changed', decision)
    },
  },
  reemit: {
    type: 'reemit',
    // Actual handling is in getEventListeners via injected reemitHandler.
    // This entry exists so the editor's useEditorProcessors discovers it.
    handler: async () => {},
  },
  // Wipes pipe runtime state (variables + persisted localStorage) and emits a
  // bus event so integrations can react — e.g. GTM clears its legacy
  // `${distributionPath}:data` mirror, trackingProperties re-captures.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  clear_all_pipe_data: {
    type: 'clear_all_pipe_data',
    handler: async ({ pipe }) => {
      pipe.clearPersistedData()
      pipe.emit('pipe:data-cleared')
    },
  },
  // Target-component processor invocation — resolves `data.targetPath` against
  // the instance registry, prefix-walking from the source's ancestry so that a
  // shared template's internal wiring finds the correct live instance (rather
  // than colliding across multiple refs to the same template).
  // eslint-disable-next-line @typescript-eslint/naming-convention
  component_processor: {
    type: 'component_processor',
    handler: async ({ args, data, pipe, sourceInstanceKey }) => {
      const processor = data?.processor as string | undefined
      const targetPath = data?.targetPath as string[] | undefined
      const config = (data?.config as Record<string, unknown> | undefined) ?? {}
      if (!processor || !targetPath || targetPath.length === 0) {
        console.warn('[Pipe] component_processor: missing processor/targetPath', data)
        return
      }

      // Source's ancestors (exclude the source's own nodeId at the tail).
      // Include the full source identity (not just its ancestors) so the
      // walk also tries the source's own scope as a prefix.  This is needed
      // for SharedNode-level events where the source IS the SharedNodeRef
      // and the target is a component *inside* that shared template — without
      // the SNR id in the prefix the target's registry key can never match.
      const sourceAncestry = sourceInstanceKey ? sourceInstanceKey.split('/') : []

      // Try longest prefix first, down to empty (page root). First hit wins.
      const candidateKeys: string[] = []
      for (let i = sourceAncestry.length; i >= 0; i--) {
        candidateKeys.push([...sourceAncestry.slice(0, i), ...targetPath].join('/'))
      }
      // Resolve-or-defer: if the target hasn't mounted yet, the dispatch is
      // queued and flushed when it registers (TTL backstop logs "target not
      // found" if it never does). Fixes onReveal/onPageShow-style events that
      // can fire before sibling components finish mounting.
      await pipe.dispatchComponentProcessor({
        candidateKeys,
        processor,
        args,
        config,
        targetPath,
        sourceInstanceKey: sourceInstanceKey ?? '',
      })
    },
  },
  // Scroll the viewport to the component identified by `data.targetPath`.
  // Looks the element up by its `data-id` attribute (set by RenderComponents);
  // for shared-template re-uses, the first occurrence in the DOM wins.
  // Integration-agnostic bridge: emits the shared tracking bus event so any
  // subscribed integration (Segment, Klaviyo, etc.) can act on it independently.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  tracking_click_event: {
    type: 'tracking_click_event',
    handler: ({ data, pipe }) => {
      const eventName = (data?.eventName as string | undefined) ?? ''

      if (!eventName) return

      pipe.emit(PAYMENT_EVENTS.trackingCustomEvent, {
        eventName,
        customData: (data?.customData as Record<string, unknown> | undefined) ?? {},
      })
    },
  },
  // Validates every input in the source's nearest form group. If any
  // invalid, returns HALT_PROCESSOR_CHAIN so the rest of the event's
  // processor chain is skipped (e.g. a `navigate` placed after this on
  // the same onClick will not run on invalid submit). No-op in EDITOR mode.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  submit_form: {
    type: 'submit_form',
    handler: async ({ pipe, sourceInstanceKey }) => {
      if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return
      const groupKey = sourceInstanceKey ? pipe.getFormGroupForSource(sourceInstanceKey) : undefined
      if (!groupKey) {
        console.warn('[Pipe] submit_form: source has no associated form group', sourceInstanceKey)
        return
      }
      const ok = await pipe.submitFormGroup(groupKey)
      if (!ok) return HALT_PROCESSOR_CHAIN
    },
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  scroll_to: {
    type: 'scroll_to',
    handler: async ({ data, pipe }) => {
      const targetPath = data?.targetPath as string[] | undefined
      const behavior = (data?.behavior as ScrollBehavior | undefined) ?? 'smooth'
      const position = (data?.position as ScrollLogicalPosition | undefined) ?? 'start'
      if (!targetPath || targetPath.length === 0) return
      // Don't fight the editor — selection / hover already drive the editor's
      // own scroll behavior; we only want this to fire in user-facing modes.
      if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return

      const targetNodeId = targetPath[targetPath.length - 1]
      if (!targetNodeId) return
      const el = document.querySelector(
        `[data-id="${CSS.escape(targetNodeId)}"]`,
      ) as HTMLElement | null
      if (el) {
        el.scrollIntoView({ behavior, block: position, inline: 'nearest' })
      } else {
        console.warn('[Pipe] scroll_to: target element not in DOM', targetPath)
      }
    },
  },
}

export function usePipeEventHandlers(pipe: PipeInterface) {
  // Injected by SharedNodeRefRenderer — null when not inside a SharedNode
  const reemitHandler = inject<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((eventName: string, ...args: any[]) => Promise<void> | void) | null
  >('reemitHandler', null)
  // Ancestry is a computed ref now (provided by SharedNodeRefRenderer) so it
  // updates when an SNR is reused with new props across page navigation.
  // We MUST read `.value` at handler-invocation time, not factory time —
  // capturing it in the closure would freeze the original ancestry and
  // produce stale source instance keys after navigation.
  const ancestry = inject<ComputedRef<readonly string[]>>(
    'sharedNodeRefAncestry',
    computed(() => []),
  )

  function getEventListeners(events: ComponentNode['events'] = {}, sourceNodeId?: string) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    const listeners: Record<string, Function> = {}

    for (const [eventName, processors] of Object.entries(events)) {
      if (!Array.isArray(processors) || processors.length === 0) continue

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      listeners[eventName] = async function handler(...args: any[]) {
        const sourceInstanceKey = sourceNodeId ? [...ancestry.value, sourceNodeId].join('/') : ''

        for (const processor of processors) {
          console.log(`Processing event '${eventName}' with processor:`, processor)
          if (!processor || !processor.type) continue

          // Error isolation: a single throwing/rejecting processor (a failing
          // analytics/integration call, a bad eval_script, a throwing reemit or
          // rule) must NOT abort the chain — later, possibly-critical processors
          // still run. Genuine throws are reported + skipped; only the
          // HALT_PROCESSOR_CHAIN *return* (not a throw) intentionally stops it.
          try {
            // Optional conditional execution — JSON-logic rule evaluated against
            // pipe data. Fail-open (evalRule returns true on error / empty rule).
            const procRules = (processor as { rules?: unknown }).rules
            if (
              procRules &&
              typeof procRules === 'object' &&
              Object.keys(procRules as object).length > 0
            ) {
              const passed = await evalRule(procRules, (k) => pipe.getData(k))
              if (!passed) continue
            }

            // Reemit: bubble event up to the parent SharedNodeRef. Await so the
            // CTA's "stay disabled until all handlers finish" contract holds when
            // the bound onClick lives on the SNR (parent) rather than locally.
            if (processor.type === 'reemit') {
              if (reemitHandler) {
                await reemitHandler(processor.data as string, ...args)
              }
              continue
            }

            // Dynamically fetch and run the processor from the Pipe registry!
            const procDef = pipe.getEventProcessor(processor.type)

            if (procDef) {
              const result = await procDef.handler({
                args,
                data: processor.data,
                pipe,
                sourceInstanceKey,
              })
              // submit_form returns this sentinel when validation fails, so
              // chained processors (e.g. a navigate after submit_form) skip.
              if (result === HALT_PROCESSOR_CHAIN) break
            } else {
              console.warn(`[Pipe] Unhandled event processor type: ${processor.type}`)
            }
          } catch (error) {
            pipe.trackError({
              error,
              context: ERROR_CONTEXTS.eventProcessor,
              metadata: { eventName, processorType: processor.type },
            })
            // Swallow and continue to the next processor.
          }
        }
      }
    }
    return listeners
  }

  return { getEventListeners }
}
